import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="city-search"
export default class extends Controller {
  static targets = ["input", "combobox", "options", "selected"]
  static values = {
    cities: Array,
    selectedCities: Array
  }

  connect() {
    if (!this.selectedCitiesValue || this.selectedCitiesValue.length === 0) {
      this.selectedCitiesValue = []
    }
    // Debug: Output cities to console
    console.log('City Search Controller - Cities loaded:', this.citiesValue)
    console.log('City Search Controller - Cities count:', this.citiesValue.length)
    
    // Sort cities alphabetically
    this.citiesValue = [...this.citiesValue].sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }))
    this.filteredCities = this.citiesValue
    console.log('City Search Controller - Cities sorted:', this.citiesValue)
    this.render()
    this.updateHiddenInputs()
    
    // Close combobox when clicking outside
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener('click', this.boundClickOutside)
    
    // Show combobox on input click or focus
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener('click', () => {
        this.showCombobox()
        this.filteredCities = this.citiesValue
        this.render()
      })
      this.inputTarget.addEventListener('focus', () => {
        this.showCombobox()
        this.filteredCities = this.citiesValue
        this.render()
      })
    }
  }

  disconnect() {
    document.removeEventListener('click', this.boundClickOutside)
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideCombobox()
    }
  }

  showCombobox() {
    if (this.hasComboboxTarget) {
      this.comboboxTarget.classList.remove("hidden")
    }
  }

  hideCombobox() {
    if (this.hasComboboxTarget) {
      this.comboboxTarget.classList.add("hidden")
    }
  }

  focus() {
    this.showCombobox()
    // Ensure cities are sorted alphabetically
    this.filteredCities = [...this.citiesValue].sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }))
    this.render()
  }

  filter(event) {
    const query = event.target.value.toLowerCase().trim()
    this.showCombobox()
    
    if (query === "") {
      // Show all cities, sorted alphabetically
      this.filteredCities = [...this.citiesValue].sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }))
    } else {
      // Filter and sort alphabetically
      this.filteredCities = this.citiesValue
        .filter(city => city.toLowerCase().includes(query))
        .sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }))
    }
    
    this.render()
  }

  select(event) {
    event.preventDefault()
    event.stopPropagation()
    const city = event.currentTarget.dataset.city
    if (!this.selectedCitiesValue.includes(city)) {
      this.selectedCitiesValue = [...this.selectedCitiesValue, city]
    }
    this.inputTarget.value = ""
    this.filteredCities = this.citiesValue
    this.hideCombobox()
    this.render()
    this.updateHiddenInputs()
    this.submitForm()
  }

  remove(event) {
    const city = event.currentTarget.dataset.city
    this.selectedCitiesValue = this.selectedCitiesValue.filter(c => c !== city)
    this.render()
    this.updateHiddenInputs()
    this.submitForm()
  }

  toggle() {
    if (this.hasComboboxTarget) {
      this.comboboxTarget.classList.toggle("hidden")
    }
  }

  updateHiddenInputs() {
    // Remove existing city inputs
    const existingInput = this.element.querySelector('input[name="city"]')
    if (existingInput) {
      existingInput.remove()
    }

    // Add a single hidden input with comma-separated selected cities
    if (this.selectedCitiesValue.length > 0) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'city'
      input.value = this.selectedCitiesValue.join(',')
      this.element.appendChild(input)
    }
  }

  submitForm() {
    const form = this.element.closest('form')
    if (form && form.dataset.controller === 'search-filter') {
      // Trigger the search-filter controller's submit method
      const event = new Event('change', { bubbles: true })
      form.dispatchEvent(event)
    }
  }

  render() {
    if (this.hasOptionsTarget) {
      this.optionsTarget.innerHTML = ""
      
      if (this.filteredCities.length === 0) {
        const li = document.createElement('li')
        li.className = "relative cursor-default select-none px-4 py-2 text-gray-700"
        li.textContent = "Keine Städte gefunden"
        this.optionsTarget.appendChild(li)
      } else {
        this.filteredCities.forEach(city => {
          if (!this.selectedCitiesValue.includes(city)) {
            const li = document.createElement('li')
            li.className = "relative cursor-default select-none px-4 py-2 text-gray-900 hover:bg-atorange-50 cursor-pointer"
            li.dataset.city = city
            li.dataset.action = "click->city-search#select"
            li.textContent = city
            this.optionsTarget.appendChild(li)
          }
        })
      }
    }

    if (this.hasSelectedTarget) {
      this.selectedTarget.innerHTML = ""
      this.selectedCitiesValue.forEach(city => {
        const span = document.createElement('span')
        span.className = "inline-flex items-center gap-x-1 rounded-md bg-atorange-100 px-2 py-1 text-sm font-medium text-atorange-800"
        span.innerHTML = `
          ${city}
          <button type="button" class="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-atorange-600 hover:bg-atorange-200 hover:text-atorange-800" data-city="${city}" data-action="click->city-search#remove">
            <span class="sr-only">Entfernen</span>
            <svg class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        `
        this.selectedTarget.appendChild(span)
      })
    }
  }
}

